/**
 * Gandolfi CMS - Vue 3 Application Entry Point
 *
 * @version 3.0.0
 * @description Main entry point for Vue 3 + Vuetify 3 + Pinia
 * @note FASE 7 — Vuex completely replaced by Pinia stores
 */

// Polyfill per Safari < 16 (findLast & co.): PRIMO import, prima di Vue/Vuetify.
// Vedi plugins/polyfills.ts (iPad non aggiornati: tendine rotte + Application Error).
import './plugins/polyfills'

import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

// Core App
import App from './App.vue'
import router from './router'

// Plugins
import vuetify from './plugins/vuetify'
import i18n from './i18n'
import { registerPermissionsDirective } from './plugins/permissions'

// Styles
import 'vuetify/styles'
import './assets/scss/main.scss'

// Icon Bundle — Pre-registers Material Symbols for offline PWA use
// MUST be imported BEFORE Vuetify mounts, so icons are in cache when components render
import './plugins/icon-bundle'

// Firebase (modular API v12)
import { initializeApp } from 'firebase/app'
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging'

// Utilities
import axios from 'axios'
import mitt from 'mitt'
import { theme } from './themes/theme-mixin'

// Pinia Stores
import { useAuthStore } from '@/stores/auth'
import { useSimulatedDateStore } from '@/stores/useSimulatedDateStore'
import { useAppStore } from '@/stores/app'
import { useNavigationStore } from '@/stores/useNavigationStore'
import { useWebsocketStore } from '@/stores/useWebsocketStore'
import { useNotificationsStore } from '@/stores/useNotificationsStore'
import { useCustomersStore } from '@/stores/useCustomersStore'
import { useProspectsStore } from '@/stores/useProspectsStore'
import { useProductsStore } from '@/stores/useProductsStore'
import { useHorecaFoodStore } from '@/stores/useHorecaFoodStore'
import { useChecklistsStore } from '@/stores/useChecklistsStore'

// Composables
import { usePushNotifications } from '@/composables/usePushNotifications'

// PWA (vite-plugin-pwa v1)
import { useRegisterSW } from 'virtual:pwa-register/vue'

// =============================================================================
// Types
// =============================================================================

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: typeof axios
    $messaging: any
    $analytics: any
    theme: typeof import('./themes/theme-mixin').theme
  }
}

// =============================================================================
// Create Vue Application
// =============================================================================

const app = createApp(App)

// =============================================================================
// Pinia Store Setup
// =============================================================================

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// =============================================================================
// Router Setup
// =============================================================================

app.use(router)

// =============================================================================
// Permissions (R0) — direttiva v-can (dopo Pinia: legge l'auth store a runtime)
// =============================================================================

registerPermissionsDirective(app)

// =============================================================================
// Vuetify Setup
// =============================================================================

app.use(vuetify)

// =============================================================================
// i18n Setup (vue-i18n 11 - Composition API only)
// =============================================================================

app.use(i18n)

// =============================================================================
// Global SFC Components (non-Vuetify aliases)
// =============================================================================

import GgDialog from '@/components/ui/GgDialog.vue'
import GgSectionCard from '@/components/ui/GgSectionCard.vue'
import GgDetailNavMenu from '@/components/ui/GgDetailNavMenu.vue'
app.component('GgDialog', GgDialog)
app.component('GgSectionCard', GgSectionCard)
app.component('GgDetailNavMenu', GgDetailNavMenu)

// =============================================================================
// Axios Configuration
// =============================================================================

axios.defaults.baseURL = import.meta.env.VITE_API_URL
axios.defaults.headers.common['Content-Type'] = 'application/json'
axios.defaults.headers.common['Accept'] = 'application/json'

// Make axios available globally (for legacy Options API components)
app.config.globalProperties.$axios = axios
app.config.globalProperties.theme = theme

// Provide axios for Composition API
app.provide('axios', axios)
app.provide('theme', theme)

// =============================================================================
// Axios Interceptors (using Pinia auth store)
// =============================================================================

// Request interceptor - Add JWT token
axios.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    // Data simulata commesse (collaudo, SOLO super-admin): header letto dal
    // middleware `commesse.simulated-date` del BE, che sposta il proprio
    // orologio per la durata della request. Limitato alle rotte /commesse.
    const simulated = useSimulatedDateStore()
    if (simulated.date && authStore.isSuperAdmin && /\/commesse(\/|\?|$)/.test(config.url ?? '')) {
      config.headers['X-Simulated-Date'] = simulated.date
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor - Handle token refresh
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

axios.interceptors.response.use(
  (response) => {
    // Echo della data simulata EFFETTIVA applicata dal BE (può differire
    // nell'orario: allineata a ultima azione + 1s nello stesso giorno)
    const simulatedEcho = response.headers['x-simulated-date']
    if (simulatedEcho) {
      useSimulatedDateStore().effectiveAt = String(simulatedEcho)
    }
    // Check for refreshed token in response header
    const newToken = response.headers['authorization']
    if (newToken && newToken.startsWith('Bearer ')) {
      const authStore = useAuthStore()
      authStore.setToken(newToken.substring(7))
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Skip if no response or not a 401
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error.response || error)
    }

    // Skip for login/refresh requests.
    // NB: `/broadcasting/auth` NON è più escluso (totem, 08/09/2026): quando
    // pusher-js riconnette con un token scaduto, l'autorizzazione del canale
    // torna 401 e senza refresh la sottoscrizione falliva in silenzio — la
    // pagina restava a video ma non riceveva più eventi. Un canale negato
    // risponde 403, non 401, quindi il refresh qui è sempre pertinente.
    if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refresh')) {
      return Promise.reject(error.response)
    }

    // Skip if already retried
    if (originalRequest._retry) {
      return Promise.reject(error.response)
    }

    const authStore = useAuthStore()

    // Check if token exists
    if (!authStore.token) {
      await authStore.resetUser()
      router.push({ name: 'login' })
      return Promise.reject(error.response)
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((newToken) => {
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`
        return axios(originalRequest)
      })
    }

    // Start refresh process. Tutta l'app in "loading" finché il refresh non
    // risponde (richiesta Enrico 08/09/2026): le altre richieste si accodano
    // (failedQueue) e ripartono con il token nuovo; se il refresh fallisce
    // (token morto: finestra jwt.refresh_ttl scaduta, o blacklist) → logout.
    originalRequest._retry = true
    isRefreshing = true
    const appStoreForRefresh = useAppStore()
    appStoreForRefresh.setLoadingState(true)

    try {
      const newToken = await authStore.refreshToken()
      processQueue(null, newToken)
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`
      return axios(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      isRefreshing = false

      // Hard logout: clear localStorage and force full page redirect
      // This avoids reactive cascade crashes when user becomes null
      // while Vue components are still mounted and accessing user properties
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('authenticated')
      console.warn('[Interceptor] Refresh token expired, forcing hard logout')
      window.location.href = '/login'
      return new Promise(() => {}) // Never resolve — page is navigating away
    } finally {
      isRefreshing = false
      appStoreForRefresh.setLoadingState(false)
    }
  },
)

// =============================================================================
// Firebase Configuration (v12)
// =============================================================================

const firebaseConfig = {
  apiKey: 'AIzaSyAP5EJOt6RDTs0LNbCC2CsQ9ub2zgB38L0',
  authDomain: 'ctf-commerciale.firebaseapp.com',
  databaseURL: 'https://ctf-commerciale.firebaseio.com',
  projectId: 'ctf-commerciale',
  storageBucket: 'ctf-commerciale.firebasestorage.app',
  messagingSenderId: '146802598688',
  appId: '1:146802598688:web:d9a89297f9c1cf32742d45',
  measurementId: 'G-52ZQSBMW26',
}

const firebaseApp = initializeApp(firebaseConfig)

// Initialize Firebase services
isMessagingSupported().then((supported) => {
  if (supported) {
    console.log('Firebase Messaging supported')
    app.config.globalProperties.$messaging = getMessaging(firebaseApp)
  }
})

// =============================================================================
// Event Emitter (mitt)
// =============================================================================

const emitter = mitt()
window.emitter = emitter
app.provide('emitter', emitter)

// =============================================================================
// PWA Service Worker Registration (vite-plugin-pwa v1)
// =============================================================================

const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onNeedRefresh() {
    console.log('New content available, please refresh.')
    const appStore = useAppStore()
    appStore.setSWRegistrationForNewContent(true)
  },
  onOfflineReady() {
    console.log('App ready to work offline.')
  },
  onRegisteredSW(swUrl: string, r: ServiceWorkerRegistration | undefined) {
    console.log('Service Worker registered:', swUrl)
    if (r) {
      // Check for SW updates every 5 minutes (was 1 hour).
      // Reduces the window where stale chunks cause MIME errors after deploy.
      setInterval(() => { r.update() }, 5 * 60 * 1000)

      // Also check when the user returns to the tab (e.g. after hours in background)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          r.update()
        }
      })
    }
  },
  onRegisterError(error: Error) {
    console.error('Service Worker registration error:', error)
  },
})

// Provide PWA utilities to components
app.provide('pwaRefresh', { needRefresh, updateServiceWorker })

// Expose updateServiceWorker globally for the app store
;(window as any).__pwaUpdateSW = updateServiceWorker

// =============================================================================
// Web Push — listen for notification-click messages from the Service Worker
// =============================================================================
// When the user taps a push notification while the PWA is already open,
// admin/public/push-sw.js posts a { type: 'PUSH_NAVIGATE', url } message to
// the focused client. We intercept it here and route through Vue Router so the
// transition is an SPA navigation (no full reload, no flash of layout).
//
// If the URL is external or the PWA is not yet open, the SW's openWindow()
// fallback handles it directly — nothing to do here.

/**
 * F4 — `?notification_read=<id>` in URL (aperto dal SW quando l'utente tocca
 * "Segna letto" senza una finestra aperta): segna letta e pulisce l'URL.
 */
function consumeNotificationReadParam(): void {
  try {
    const url = new URL(window.location.href)
    const id = url.searchParams.get('notification_read')
    if (!id) return
    url.searchParams.delete('notification_read')
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    useNotificationsStore().markRead(id).catch(() => {})
  } catch {
    // URL non parsabile: ignora
  }
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event?.data
    if (!data) return

    // F4 — azione "Segna letto" dalla notifica push
    if (data.type === 'PUSH_MARK_READ' && typeof data.id === 'string') {
      useNotificationsStore().markRead(data.id).catch(() => {})
      return
    }
    // F4 — la subscription push è cambiata: re-registra l'endpoint corrente (con JWT)
    if (data.type === 'PUSH_RESUBSCRIBE') {
      void usePushNotifications().reattachIfPermitted()
      return
    }

    if (data.type !== 'PUSH_NAVIGATE' || typeof data.url !== 'string') return

    try {
      const target = new URL(data.url, window.location.origin)
      // Only handle same-origin URLs through the router; external URLs are a no-op
      // (the SW already opened them in a new window).
      if (target.origin !== window.location.origin) return
      const path = target.pathname + target.search + target.hash
      router.push(path).catch((err) => {
        console.warn('[Push] router.push failed for', path, err)
      })
    } catch (err) {
      console.warn('[Push] invalid PUSH_NAVIGATE payload:', data, err)
    }
  })
}

// =============================================================================
// Global Error Handler
// =============================================================================

app.config.errorHandler = (err, instance, info) => {
  console.error('Global Vue Error:', err)
  console.error('Component:', instance)
  console.error('Info:', info)
}

window.onerror = (msg, url, line, col, error) => {
  console.error('Window Error:', { msg, url, line, col, error })
}

// =============================================================================
// Auth Store Hooks — Wire websocket + push side-effects
// =============================================================================

function setupAuthHooks(): void {
  const authStore = useAuthStore()
  const wsStore = useWebsocketStore()
  const push = usePushNotifications()

  authStore.setHooks({
    onLogin: async () => {
      if (import.meta.env.VITE_USE_PUSHER === '1') {
        wsStore.reconnect()
      }
      wsStore.listenToPrivateChannel()
      wsStore.listenToCommonChannel()
      wsStore.listenToUserChannel() // F4: campanella realtime

      // Silently reassign any existing PushSubscription to the new user.
      // No-op if the browser has never been granted permission or has no cached subscription.
      // Critical for multi-user-per-device (e.g. shared tablet, browser used by tech then admin):
      // without this, the previous user keeps receiving pushes on this device.
      void push.reattachIfPermitted()
    },
    beforeLogout: async () => {
      // Detach server-side BEFORE the JWT is invalidated, otherwise the
      // authenticated POST /push/unsubscribe would return 401 and the row
      // would be left orphaned, sending pushes for the logged-out user.
      // The browser PushSubscription is preserved — next user reattaches silently.
      await push.detachServerSide()
    },
    onLogout: () => {
      if (import.meta.env.VITE_USE_PUSHER === '1') {
        wsStore.disconnect()
      }
      // Reset highlighted counts to avoid stale badges after re-login with a different user
      useProspectsStore().prospectsHighlighted = 0
      useCustomersStore().customersHighlighted = 0
      // E6: la voce CHECKLIST non deve restare accesa per l'utente successivo
      useChecklistsStore().reset()
    },
    onUserLoaded: () => {
      wsStore.listenToPrivateChannel()
      wsStore.listenToCommonChannel()
      wsStore.listenToUserChannel() // F4: campanella realtime
      consumeNotificationReadParam()

      // Same reattach as onLogin — covers the page-reload-with-existing-session case
      // where me() restores the user but onLogin was not fired (e.g. user comes back
      // hours later, JWT still valid, app boot triggers me() instead of login()).
      void push.reattachIfPermitted()
    },
  })
}

// =============================================================================
// Router Guards (using Pinia stores)
// =============================================================================

router.beforeEach(async (to, _from) => {
  const authStore = useAuthStore()
  const navStore = useNavigationStore()
  const appStore = useAppStore()

  // Set fullscreen page mode
  appStore.setPageFullScreen(to.meta.fullscreenPage || false)

  // Auto-switch to 'srv' company when entering /tech/ routes
  if (to.fullPath.startsWith('/tech/') && navStore.currentCompany !== 'srv') {
    navStore.changeCompany('srv')
    console.log('[Router] Auto-switch company to srv for tech route')
  }

  // Routes requiring authentication
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const user = authStore.user

    // Not authenticated → redirect to login
    if (user == null) {
      // `warn` e non `log`: e' l'unico redirect che nasconde all'utente la
      // pagina che ha chiesto, ed e' cio' che si va a cercare quando qualcuno
      // "viene sloggiato da solo". I console.log spariscono dalla build
      // (pure_funcs), questo deve restare.
      console.warn('[Router] User not authenticated, redirecting to login')
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // R1 — Route gated sui permessi (meta.permissionsAny): serve ALMENO UNO
    // dei permessi elencati; speculare al filtro voci menu in useNavigationStore
    const requiredAny = to.matched.flatMap((record) => record.meta.permissionsAny ?? [])
    if (requiredAny.length > 0 && !authStore.canAny(...requiredAny)) {
      console.log('[Router] Missing permission for route', to.fullPath, requiredAny)
      return { name: 'dashboard' }
    }

    // E6 — rotte aperte a tutti i ruoli (checklist affidate): nessun
    // confinamento per area, chi le compila può essere chiunque
    const openToAll = to.matched.some((record) => record.meta.openToAllRoles)

    // CST role: can only access /tech/cst or /
    if (!openToAll && user.role === 'cst' && !(to.fullPath.startsWith('/tech/cst') || to.fullPath === '/')) {
      console.log('[Router] CST user restricted to /tech/cst')
      return { name: 'dashboard' }
    }

    // TECH role: can only access /tech/* routes
    if (!openToAll && to.fullPath !== '/' && user.role === 'tech' && !to.fullPath.startsWith('/tech')) {
      console.log('[Router] Tech user restricted to /tech routes')
      return { name: 'dashboard' }
    }

    // R1 (19/08, de-roling): la vecchia guard role-based "TECH non-boss non
    // accede a /technicians e /tech/reports" è sostituita da meta.permissionsAny
    // sulle rotte (technicians.* → technicians.manage, tech/reports/* →
    // reports.tech.view|occupation), gestita dal blocco generico qui sopra.

    // COMMERCIAL role: can only access /commercial/* routes
    if (!openToAll && to.fullPath !== '/' && user.role === 'commercial' && !to.fullPath.startsWith('/commercial')) {
      console.log('[Router] Commercial user restricted to /commercial routes')
      return { name: 'dashboard' }
    }

    // COMMERCIAL without sectors: redirect to dashboard
    if (
      !openToAll &&
      to.fullPath !== '/' &&
      user.role === 'commercial' &&
      (user.sectors == null || user.sectors.length === 0)
    ) {
      console.log('[Router] Commercial user has no sectors')
      return { name: 'dashboard' }
    }

    // Censuses page: only available for company 'mkt'
    if (to.fullPath !== '/' && to.fullPath.includes('/censuses') && navStore.currentCompany !== 'mkt') {
      console.log('[Router] Censuses only available for mkt company')
      return { name: 'dashboard' }
    }

    // HORECA-FOOD management routes: gating MKT6 verificato lato BE.
    // Admin/super-admin passano; gli agenti devono essere abilitati al settore.
    if (to.matched.some((record) => record.meta.requiresHorecaFoodManage)) {
      const horecaFoodStore = useHorecaFoodStore()
      const can = horecaFoodStore.canManage ?? (await horecaFoodStore.fetchCanManage())
      if (!can) {
        console.log('[Router] HORECA-FOOD management forbidden for current user')
        return { name: 'dashboard' }
      }
    }

    // Gating generico per ruolo (meta.roles): es. report admin-only.
    const allowedRoles = to.meta.roles
    if (Array.isArray(allowedRoles) && allowedRoles.length > 0 && !allowedRoles.includes(user.role ?? '')) {
      console.log('[Router] Route forbidden for role', user.role)
      return { name: 'dashboard' }
    }

    // Update navigation state and proceed
    navStore.setCurrentRoute(to)
    return true
  }

  // Guest routes (login, register, etc.)
  if (to.matched.some((record) => record.meta.guest)) {
    if (authStore.user == null) {
      navStore.setCurrentRoute(to)
      return true
    } else {
      // Authenticated user trying to access guest page → dashboard
      return { name: 'dashboard' }
    }
  }

  // Public routes
  navStore.setCurrentRoute(to)
  return true
})

// After each navigation - update toolbar state
router.afterEach(() => {
  const navStore = useNavigationStore()
  navStore.updateToolbar()
})

// =============================================================================
// Store Watchers (Pinia reactive — replaces Vuex store.watch)
// =============================================================================

function setupStoreWatchers(): void {
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const navStore = useNavigationStore()

  // Watch user changes → load preferences and update navigation
  watch(
    () => authStore.user,
    (user) => {
      appStore.loadUserPreferences()
      navStore.updateNavigation()
      console.log('[main.ts] User changed:', user?.email ?? 'null')

      if (user != null) {
        // Set language based on user email
        const lang = user.email === 'cst@francese.fr' ? 'fr' : 'it'
        appStore.changeLanguage(lang)
        i18n.global.locale.value = lang
      }
    },
  )

  // Watch company changes → refresh data if on relevant pages
  watch(
    () => navStore.currentCompany,
    (_company) => {
      const currentPath = router.currentRoute.value.fullPath

      if (currentPath.indexOf('/contacts/customers') === 0) {
        useCustomersStore().fetchCustomers().catch(() => {})
      }
      if (currentPath.indexOf('/products') === 0) {
        useProductsStore().fetchProducts().catch(() => {})
      }
    },
  )
}

// =============================================================================
// Application Initialization
// =============================================================================

async function initApp() {
  try {
    // Setup auth hooks for websocket integration
    setupAuthHooks()

    // Setup reactive watchers
    setupStoreWatchers()

    // Initialize app state
    const appStore = useAppStore()
    appStore.initDeviceID()
    appStore.initCurrentLanguage()

    // Load session from localStorage
    const authStore = useAuthStore()
    authStore.loadSession()

    // If we have a token, verify it's still valid
    if (authStore.token) {
      try {
        await authStore.me()
        console.log('Session restored successfully')

        // Legacy company redirect: sys → mkt (migrated from Vue 2 main.ts)
        const navStore = useNavigationStore()
        if ((navStore.currentCompany as string) === 'sys') {
          navStore.setCurrentCompany('mkt')
          console.log('[init] sys → mkt')
        }

      } catch (error) {
        // If the interceptor already triggered a hard redirect (window.location.href),
        // this catch will still fire but the page is already navigating away.
        //
        // `error` va LOGGATO e non ingoiato: senza, qualunque guasto qui
        // dentro diventa un "sessione scaduta" muto. Il realtime e' stato
        // spostato fuori da questo try e l'hook onUserLoaded ha ora il proprio
        // catch dentro me(), quindi ci resta davvero solo l'autenticazione.
        console.warn('Session expired, redirecting to login —', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('authenticated')
      }

      // Realtime FUORI dal try della sessione, e con un catch proprio.
      // Stava dentro, quindi un Reverb spento (in locale è la norma: la
      // connessione viene rifiutata) o un canale che rifiuta l'auth veniva
      // trattato come "sessione scaduta" e cancellava il token da
      // localStorage. Un guasto del realtime non è un problema di
      // autenticazione e non deve sloggiare nessuno: al massimo si resta
      // senza aggiornamenti in tempo reale.
      if (authStore.user != null && import.meta.env.VITE_USE_PUSHER === '1') {
        try {
          const wsStore = useWebsocketStore()
          wsStore.connect()
          // Gli hook sono già scattati prima che Echo esistesse
          // (onUserLoaded da me()): le sottoscrizioni vanno rifatte ora.
          wsStore.listenToPrivateChannel()
          wsStore.listenToCommonChannel()
          wsStore.listenToUserChannel() // F4: campanella realtime
        } catch (error) {
          console.error('WebSocket non disponibile (sessione non toccata):', error)
        }
      }
    }
  } catch (error) {
    console.error('App initialization error:', error)
  }

  // Mount the app
  app.mount('#app')

  // Hide splash screen after mount
  setTimeout(() => {
    const appStore = useAppStore()
    appStore.setSplashShown(true)
  }, 2000)

  const appStore = useAppStore()
  console.log(`🚀 Gandolfi CMS v${appStore.appVersion} initialized`)
}

// Start the application
initApp()
